import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function GoogleCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Completing Google sign-in...');

  useEffect(() => {
    const handleCallback = async () => {
      // Get OAuth parameters from URL
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      // Check for OAuth errors
      if (error) {
        setStatus('error');
        setMessage(
          error === 'access_denied'
            ? 'You cancelled the Google sign-in. Please try again.'
            : 'An error occurred during Google sign-in. Please try again.'
        );
        setTimeout(() => navigate('/login?error=oauth_failed'), 3000);
        return;
      }

      // Validate required parameters
      if (!code || !state) {
        setStatus('error');
        setMessage('Invalid OAuth callback. Missing required parameters.');
        setTimeout(() => navigate('/login?error=oauth_failed'), 3000);
        return;
      }

      // Validate state parameter (CSRF protection)
      const storedState = sessionStorage.getItem('oauth_state');
      if (!storedState || storedState !== state) {
        setStatus('error');
        setMessage('Invalid state parameter. Possible CSRF attack detected.');
        setTimeout(() => navigate('/login?error=oauth_failed'), 3000);
        return;
      }

      // Clear stored state
      sessionStorage.removeItem('oauth_state');

      try {
        // Exchange code for session
        const response = await fetch(
          `/auth/google/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
          {
            method: 'GET',
            credentials: 'include',
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.error?.message || 'Failed to complete Google sign-in'
          );
        }

        // Success!
        setStatus('success');
        setMessage('Sign-in successful! Redirecting...');

        // Redirect to dashboard
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      } catch (err) {
        setStatus('error');
        setMessage(
          err instanceof Error ? err.message : 'An error occurred during sign-in'
        );
        setTimeout(() => navigate('/login?error=oauth_failed'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#282828] via-[#4c1d95] to-[#282828]">
      <div className="max-w-md w-full p-10 bg-[#282828]/80 backdrop-blur-sm rounded-2xl border border-[#1283EB]/30 shadow-2xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/aiia-logo-white.svg"
            alt="AIIA Logo"
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-xl text-[#F8F3EC] font-semibold">
            Partner Portal
          </h1>
        </div>

        {/* Status Message */}
        {status === 'loading' && (
          <div className="text-center space-y-4">
            <Loader2
              className="h-12 w-12 mx-auto text-[#1283EB] animate-spin"
              aria-hidden="true"
            />
            <p className="text-[#F8F3EC] font-medium">{message}</p>
            <p className="text-sm text-white/60">Please wait...</p>
          </div>
        )}

        {status === 'success' && (
          <Alert className="bg-green-900/20 border-green-800 text-green-400">
            <CheckCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <Alert variant="destructive" className="bg-red-900/20 border-red-800 text-red-400">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
            <p className="text-center text-sm text-white/60">
              Redirecting to login page...
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default GoogleCallback;
